import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, CircularProgress, IconButton, Menu, MenuItem } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../../hook';
import { ClientConfirmDialog } from './ClientConfirmDialog';
import { getHomeSummary } from './clientApi';
import './client.scss';

const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};
const pen = (value) => `S/ ${Number(value || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const usd = (value) => `USD ${Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function Home() {
  const navigate = useNavigate();
  const { auth, logout } = useAuth();
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [logoutOpen, setLogoutOpen] = useState(false);

  useEffect(() => {
    let active = true;
    getHomeSummary(auth.token, currentMonth())
      .then((data) => { if (active) setSummary(data); })
      .catch((err) => { if (active) setError(err.message); });
    return () => { active = false; };
  }, [auth.token]);

  const confirmLogout = () => { setLogoutOpen(false); logout(); };
  const go = (path) => navigate(path);
  const recurring = summary?.recurring_sales;
  const shortcuts = [
    { section: 'VENTAS', items: [
      { label: 'Nueva venta', icon: <PointOfSaleIcon />, path: '/client/nueva-venta' },
      { label: 'Añadir pago', icon: <PaymentsOutlinedIcon />, path: '/client/actualizar-venta' },
      { label: 'Venta recurrente', icon: <AutorenewIcon />, path: '/client/ventas-recurrentes', badge: recurring?.total ? `${recurring.pending}/${recurring.total}` : null, badgeColor: recurring?.pending ? 'error' : 'success' },
    ] },
    { section: 'GASTOS', items: [
      { label: 'Nuevo gasto', icon: <ReceiptLongOutlinedIcon />, path: '/client/nuevo-gasto' },
      { label: 'Gasto recurrente', icon: <EventRepeatIcon />, path: '/client/nuevo-gasto?seccion=recurrentes' },
    ] },
    { section: 'CRM', items: [
      { label: 'Actualizar leads', icon: <ManageAccountsOutlinedIcon />, path: '/client/leads' },
      { label: 'Nuevo lead', icon: <PersonAddAltOutlinedIcon />, path: '/client/leads?nuevo=1' },
    ] },
  ];

  return <main className="client-page client-home">
    <header className="client-home-header">
      <IconButton aria-label="Abrir menú" onClick={(event) => setMenuAnchor(event.currentTarget)}><MenuIcon /></IconButton>
      <img src="/logo-white.svg" alt="Cloux" />
      <span />
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => go('/client/resumen-general')}><DashboardOutlinedIcon /> Resumen general</MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); setLogoutOpen(true); }}><LogoutIcon /> Cerrar sesión</MenuItem>
      </Menu>
    </header>

    <div className="client-home-content">
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <section className="client-home-indicators">
        <div className="client-home-section-title"><span>Indicadores del mes</span></div>
        {!summary ? <div className="client-home-loading"><CircularProgress size={28} /></div> : <div className="client-home-kpis">
          <button type="button" onClick={() => go('/client/resumen-general')}>
            <span>Ventas netas</span><strong>{pen(summary.sales.net_pen)}</strong><small>{summary.sales.count} venta(s) · sin IGV</small>
          </button>
          <button type="button" onClick={() => go('/client/resumen-general')}>
            <span>Ingresos cobrados</span><strong>{pen(summary.collections.net_pen)}</strong><b>{usd(summary.collections.net_usd)}</b><small>{summary.collections.payments_count} pago(s) · sin IGV</small>
          </button>
        </div>}
      </section>

      {shortcuts.map((group) => <section className="client-home-group" key={group.section}>
        <div className="client-home-section-title"><span>{group.section}</span></div>
        <div className="client-home-grid">
          {group.items.map((item) => <button type="button" key={item.label} onClick={() => go(item.path)}>
            <Badge badgeContent={item.badge} invisible={!item.badge} color={item.badgeColor || 'error'}>
              <span className="client-home-grid__icon">{item.icon}</span>
            </Badge>
            <strong>{item.label}</strong>
          </button>)}
        </div>
      </section>)}
    </div>

    <nav className="client-home-nav" aria-label="Accesos rápidos">
      <button type="button" onClick={() => go('/client/resumen-general')}><DashboardOutlinedIcon /><span>Resumen</span></button>
      <button type="button" onClick={() => go('/client/actualizar-venta')}><EditNoteOutlinedIcon /><span>Actualizar venta</span></button>
      <button type="button" onClick={() => go('/client/leads')}><GroupsOutlinedIcon /><span>Leads</span></button>
    </nav>

    <ClientConfirmDialog open={logoutOpen} title="¿Cerrar sesión?" message="Tendrás que ingresar nuevamente tus credenciales para acceder." confirmText="Sí, cerrar sesión" onCancel={() => setLogoutOpen(false)} onConfirm={confirmLogout} />
  </main>;
}
